import { CalendarClock, CheckSquare, Square } from "@tamagui/lucide-icons";
import type { Timestamp } from "firebase/firestore";
import { action, computed, makeObservable, observable, override } from "mobx";
import { TKey } from "../../../shared/translations/translationKeys";
import { ActivityEventType } from "../../activities/types";
import type { Api } from "../../core/apis";
import type { Stores } from "../../core/stores";
import BaseModuleModel, {
  type ModuleModelData,
  type Snapshot,
} from "../core/BaseModuleModel";
import {
  DateDueModuleBlockUI,
  DateDueModuleSetupUI,
  DateDueModuleUI,
} from "./DateDueModuleUI";

export type DateDueModuleSnapshot = {
  dateStart?: Timestamp;
  dateEnd?: Timestamp;
  assignedTo?: string[];
  completedBy?: string[];
};

export interface DateDueModuleModelData
  extends ModuleModelData<DateDueModuleSnapshot> {
  text: string;
  dateStart: Timestamp | null;
  dateEnd: Timestamp | null;
  assignedTo: string[];
  completedBy: string[];
}

export default class DateDueModuleModel extends BaseModuleModel<
  DateDueModuleSnapshot,
  DateDueModuleModelData
> {
  constructor(stores: Stores, api: Api, data: DateDueModuleModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      text: data.text ?? "",
      dateStart: data.dateStart ?? null,
      dateEnd: data.dateEnd ?? null,
      assignedTo: data.assignedTo ?? [],
      completedBy: data.completedBy ?? [],
    });

    makeObservable(this);
  }

  // Used to show setting
  hasHelperGuide = false;

  @action async updateSnapshotWithoutSave() {
    if (this.data.dateStart) {
      this.data.snapshot = {
        dateStart: this.data.dateStart,
        assignedTo: this.data.assignedTo,
        completedBy: this.data.completedBy,
      };
      if (this.data.dateEnd) {
        this.data.snapshot.dateEnd = this.data.dateEnd;
      }
    } else {
      this.data.snapshot = {};
    }
  }

  updateModuleSnapshot() {
    this.updateSnapshotWithoutSave();
    this.api.modulesApi.update(this.uid, {
      snapshot: this.data.snapshot,
    });
  }

  @observable showFormSheet = false;

  @action toggleFormSheet() {
    console.info("toggle form");
    this.showFormSheet = !this.showFormSheet;
    this.setActivitySheetOpen(!this.showFormSheet);
  }

  @computed get infoRowItems() {
    if (!this.data.assignedTo.length) {
      return undefined;
    }

    const completedBy = this.data.assignedTo?.filter((assignedToUserUid) => {
      return this.data.completedBy?.includes(assignedToUserUid);
    });

    const notCompletedBy = this.data.assignedTo?.filter((assignedToUserUid) => {
      return !completedBy?.includes(assignedToUserUid);
    });

    const items = [];

    if (completedBy.length) {
      items.push({
        icon: CheckSquare,
        text: completedBy
          .map((uid) => {
            return (
              this.stores.usersStore.byId.get(uid)?.data.displayName ??
              "Unknown"
            );
          })
          .join(", "),
      });
    }

    if (notCompletedBy.length) {
      items.push({
        icon: Square,
        text: notCompletedBy
          .map((uid) => {
            return (
              this.stores.usersStore.byId.get(uid)?.data.displayName ??
              "Unknown"
            );
          })
          .join(", "),
      });
    }

    return items;
  }

  @action async updateDates(
    dateStart: Timestamp | null,
    dateEnd: Timestamp | null,
  ) {
    this.data.dateStart = dateStart;
    this.data.dateEnd = dateEnd;

    this.updateSnapshotWithoutSave();
    this.save();
    this.sendTelemetryEvent("updated_dates");

    this.addEmailReminders(
      this.plan.uid,
      this.plan.data.title,
      this.uid,
      this.data.dateStart,
    );
  }

  @action async toggleCompleted() {
    const userUid = this.userUid;

    if (!userUid) {
      return;
    }

    const completed = !this.data.completedBy.includes(userUid);
    if (completed) {
      this.data.completedBy.push(userUid);
    } else {
      this.data.completedBy = this.data.completedBy.filter(
        (uid) => uid !== userUid,
      );
    }

    this.postActivityEvent(
      ActivityEventType.DataChanged,
      completed ? TKey.ActivityDateDueCompleted : TKey.ActivityDateDueReset,
      {},
    );

    this.updateTimestampWithoutSave();
    this.updateSnapshotWithoutSave();
    this.save();
  }

  setText = async (text: string) => {
    const oldValue = this.data.text;
    if (oldValue !== text) {
      this.updateTimestampWithoutSave();
      this.data.text = text;
      await this.api.modulesApi.set(this);

      this.postActivityEvent(
        ActivityEventType.DataChanged,
        TKey.ActivityDateDueDescriptionUpdated,
        {
          oldValue,
          newValue: this.data.text,
        },
      );

      this.updateModuleSnapshot();
      this.sendTelemetryEvent("text_updated");
    }
  };

  @computed get isOngoing() {
    if (this.data.dateStart && this.data.dateEnd) {
      return (
        Date.now() > this.data.dateStart.toMillis() &&
        Date.now() < this.data.dateEnd.toMillis()
      );
    }
    return false;
  }

  @override get isDone() {
    return this.data.completedBy.length > 0;
  }

  @computed get isDue() {
    if (this.data.dateEnd) {
      return Date.now() > this.data.dateEnd.toMillis();
    }

    if (this.data.dateStart) {
      return Date.now() > this.data.dateStart.toMillis();
    }

    return false;
  }

  @computed get isCompletedByUser() {
    if (!this.userUid) {
      return false;
    }

    return this.data.completedBy.includes(this.userUid);
  }

  @computed get isCompleted() {
    if (this.data.assignedTo.length > 0) {
      return this.completedStatus.count === this.completedStatus.total;
    }

    return this.data.completedBy.length > 0;
  }

  @computed get userCanComplete() {
    if (this.data.assignedTo.length === 0) {
      return true;
    }

    if (!this.userUid) {
      return false;
    }

    return this.data.assignedTo.includes(this.userUid);
  }

  @computed get completedStatus() {
    return {
      count: this.data.completedBy.filter((cUid) =>
        this.data.assignedTo.includes(cUid),
      ).length,
      total: this.data.assignedTo.length,
    };
  }

  @action toggleAssignee(userUid: string) {
    const displayName =
      this.stores.usersStore.byId.get(userUid)?.data.displayName;

    if (this.data.assignedTo.includes(userUid)) {
      this.data.assignedTo = this.data.assignedTo.filter((a) => a !== userUid);

      if (displayName) {
        this.postActivityEvent(
          ActivityEventType.DataChanged,
          TKey.ActivityDateDueAssignedRemoved,
          {
            label: this.data.title ?? "",
            displayName,
          },
        );
      }
    } else {
      this.data.assignedTo.push(userUid);
    }

    this.api.modulesApi.set(this);

    if (displayName) {
      this.postActivityEvent(
        ActivityEventType.DataChanged,
        TKey.ActivityDateDueAssignedTo,
        {
          label: this.data.title ?? "",
          displayName,
        },
      );
    }

    this.updateModuleSnapshot();
  }

  render() {
    return <DateDueModuleUI module={this} />;
  }

  renderStatic() {
    return null;
  }

  renderBlock() {
    return <DateDueModuleBlockUI module={this} />;
  }

  renderStaticBlock() {
    return this.renderBlock();
  }

  renderIcon(size?: number) {
    return <CalendarClock size={size} />;
  }

  renderBlockSetup = () => {
    return <DateDueModuleSetupUI module={this} />;
  };

  static is(
    module: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>,
  ): module is DateDueModuleModel {
    return module instanceof DateDueModuleModel;
  }
}
